import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FlowState } from '@de/providers/dynamicExperienceReducer';
import type { Option, Step } from '@de/types/types';
import {
  canPrefetchStep,
  type DynamicOptionsCache,
  generateCacheKey,
  getStepIdsAffectingDynamicOptionsPrefetch,
  prefetchEligibleSteps,
} from '@de/utils/data-fetch/prefetchDynamicOptions';

export interface UseDynamicOptionsCacheProps {
  steps: Step[];
  flowState: FlowState;
  enabled?: boolean;
}

export interface UseDynamicOptionsCacheReturn {
  cache: DynamicOptionsCache;
  isPrefetching: boolean;
  getCachedOptions: (step: Step) => Option[] | undefined;
  /** Identity is stable when only `cache` state changes (reads latest cache via `cacheRef`). */
  prefetchAll: () => Promise<void>;
  invalidateStep: (stepId: string) => void;
  clearCache: () => void;
}

/**
 * Cache manager for dynamic option fetch results. Keys entries by dependency
 * values and invalidates when dependent flowState keys change. Multiple
 * question instances with different dependency sets maintain isolated caches
 * via separate hook instances.
 */
export const useDynamicOptionsCache = ({
  steps,
  flowState,
  enabled = true,
}: UseDynamicOptionsCacheProps): UseDynamicOptionsCacheReturn => {
  const [cache, setCache] = useState<DynamicOptionsCache>({});
  // Seed `true` when a prefetch is already expected so the first render signals
  // "pending" before any effect runs. Child consumers commit their effects before
  // this provider's prefetch effect, so a `false` seed would let them treat the
  // pre-prefetch window as a settled cache miss and fire a redundant fetch.
  const [isPrefetching, setIsPrefetching] = useState(
    () =>
      enabled && steps.some((step) => canPrefetchStep(step, flowState, steps)),
  );
  const cacheKeysRef = useRef<Record<string, string>>({});
  /** Always matches latest `cache` so `prefetchAll` does not close over a stale snapshot. */
  const cacheRef = useRef<DynamicOptionsCache>(cache);
  cacheRef.current = cache;
  /** Superseded prefetches must not call `setCache` / clear `isPrefetching` incorrectly. */
  const prefetchGenerationRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const stepsById = useMemo(() => {
    const m = new Map<string, Step>();
    for (const s of steps) {
      if (s.id) {
        m.set(s.id, s);
      }
    }
    return m;
  }, [steps]);

  const invalidateStaleCacheEntries = useCallback(
    (currentCache: DynamicOptionsCache): DynamicOptionsCache => {
      const staleStepIds: string[] = [];

      Object.keys(currentCache).forEach((stepId) => {
        const step = stepsById.get(stepId);
        if (!step) {
          // Step removed from flow (e.g. steps list changed); remove orphaned entry.
          staleStepIds.push(stepId);
        } else {
          const currentKey = generateCacheKey(step, flowState, steps);
          const cachedKey = cacheKeysRef.current[stepId];

          if (currentKey !== cachedKey) {
            staleStepIds.push(stepId);
          }
        }
      });

      if (staleStepIds.length === 0) {
        return currentCache;
      }

      const freshCache = { ...currentCache };
      staleStepIds.forEach((stepId) => {
        delete freshCache[stepId];
        delete cacheKeysRef.current[stepId];
      });

      return freshCache;
    },
    [steps, flowState, stepsById],
  );

  const prefetchAll = useCallback(async () => {
    if (!enabled || steps.length === 0) {
      return;
    }

    const generation = ++prefetchGenerationRef.current;

    if (!isMountedRef.current) {
      return;
    }

    setIsPrefetching(true);

    try {
      const freshCache = invalidateStaleCacheEntries(cacheRef.current);

      const updatedCache = await prefetchEligibleSteps(
        steps,
        flowState,
        freshCache,
      );

      if (
        !isMountedRef.current ||
        generation !== prefetchGenerationRef.current
      ) {
        return;
      }

      Object.keys(updatedCache).forEach((stepId) => {
        const step = stepsById.get(stepId);
        if (step) {
          cacheKeysRef.current[stepId] = generateCacheKey(
            step,
            flowState,
            steps,
          );
        }
      });

      setCache(updatedCache);
    } catch (error) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[DynamicOptionsCache] Prefetch error:', error);
      }
    } finally {
      if (
        isMountedRef.current &&
        generation === prefetchGenerationRef.current
      ) {
        setIsPrefetching(false);
      }
    }
  }, [enabled, steps, flowState, stepsById, invalidateStaleCacheEntries]);

  const getCachedOptions = useCallback(
    (step: Step): Option[] | undefined => {
      if (!step.id) {
        return undefined;
      }

      const cachedOptions = cache[step.id];
      if (!cachedOptions) {
        return undefined;
      }

      const currentKey = generateCacheKey(step, flowState, steps);
      const cachedKey = cacheKeysRef.current[step.id];

      if (currentKey !== cachedKey) {
        return undefined;
      }

      return cachedOptions;
    },
    [cache, flowState, steps],
  );

  const invalidateStep = useCallback((stepId: string) => {
    setCache((prev) => {
      const { [stepId]: _, ...rest } = prev;
      return rest;
    });
    delete cacheKeysRef.current[stepId];
  }, []);

  const clearCache = useCallback(() => {
    setCache({});
    cacheKeysRef.current = {};
  }, []);

  const prefetchDependencyStepIds = useMemo(
    () => getStepIdsAffectingDynamicOptionsPrefetch(steps),
    [steps],
  );

  // Only dependency-driving flowState slices should reschedule prefetch (not every keystroke).
  const prefetchFlowSignature = useMemo(
    () =>
      JSON.stringify(
        Object.fromEntries(
          prefetchDependencyStepIds.map((id) => [id, flowState[id] ?? null]),
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only dependency-source steps; not whole flowState
    [
      prefetchDependencyStepIds,
      ...prefetchDependencyStepIds.map((id) => flowState[id]),
    ],
  );

  // Single debounced prefetch on mount and when enabled, steps, or relevant answers change.
  // prefetchAll omitted from deps to avoid re-running when only cache updates.
  // `prefetchAll` reads latest cache via cacheRef so the timeout callback is not stale.
  useEffect(() => {
    if (!enabled) return;

    const timeoutId = setTimeout(() => {
      prefetchAll();
    }, 50);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit prefetchAll to avoid effect on cache updates
  }, [enabled, steps, prefetchFlowSignature]);

  return {
    cache,
    isPrefetching,
    getCachedOptions,
    prefetchAll,
    invalidateStep,
    clearCache,
  };
};

export default useDynamicOptionsCache;
